import { selectedBackend } from './api';

export function encodeInput(bytes: Uint8Array): string {
  let bin = '';
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return JSON.stringify({ type: 'input', data: btoa(bin) });
}

export function encodeResize(cols: number, rows: number): string {
  return JSON.stringify({ type: 'resize', cols, rows });
}

export function nextBackoff(attempt: number): number {
  return Math.min(100 * 2 ** attempt, 3200);
}

// ConnStatus drives the on-screen connection indicator. 'offline' is reported
// once reconnect attempts have backed off to the cap, signalling a likely
// down tunnel/laptop rather than a transient blip.
export type ConnStatus = 'connecting' | 'connected' | 'reconnecting' | 'offline';

// OFFLINE_AFTER is the reconnect attempt count at which we stop saying
// "reconnecting" and surface "offline" instead.
export const OFFLINE_AFTER = 5;

interface Handlers {
  onOutput: (bytes: Uint8Array) => void;
  onExit: () => void;
  onStatus?: (s: ConnStatus) => void;
}

export class TerminalSocket {
  private ws?: WebSocket;
  private attempt = 0;
  private closed = false;

  constructor(
    private sessionId: string,
    private h: Handlers
  ) {
    this.h.onStatus?.('connecting');
    this.connect();
  }

  private url(): string {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    // Browsers cannot set custom headers on WebSocket connections, so the
    // chosen backend rides along as a ?backend= query param.
    const id = selectedBackend();
    const q = id ? `?backend=${encodeURIComponent(id)}` : '';
    return `${proto}://${location.host}/api/sessions/${this.sessionId}/ws${q}`;
  }

  private connect() {
    const ws = new WebSocket(this.url());
    ws.binaryType = 'arraybuffer';
    this.ws = ws;
    ws.onopen = () => {
      this.attempt = 0;
      this.h.onStatus?.('connected');
    };
    ws.onmessage = (ev) => {
      if (typeof ev.data === 'string') {
        const msg = JSON.parse(ev.data);
        if (msg.type === 'exit') this.h.onExit();
        return;
      }
      this.h.onOutput(new Uint8Array(ev.data as ArrayBuffer));
    };
    ws.onclose = () => {
      if (this.closed) return;
      const a = this.attempt++;
      this.h.onStatus?.(a >= OFFLINE_AFTER ? 'offline' : 'reconnecting');
      setTimeout(() => this.connect(), nextBackoff(a));
    };
  }

  sendInput(bytes: Uint8Array) {
    this.ws?.readyState === WebSocket.OPEN && this.ws.send(encodeInput(bytes));
  }
  sendResize(cols: number, rows: number) {
    this.ws?.readyState === WebSocket.OPEN && this.ws.send(encodeResize(cols, rows));
  }
  close() {
    this.closed = true;
    this.ws?.close();
  }
}
